// eslint-disable-next-line import/no-cycle
import { isHtmlElement, isDocumentFragment, isJsxCatalogItem } from '../utilities/guards';
import { parseProps } from './jsx/jsxPropParser';
// eslint-disable-next-line import/no-cycle
import CatalogItem from './catalogItem';
/**
 * Tests to see if passed element is a node tag
 *
 * @param nodeTag Element to test
 */
const isNodeTag = (nodeTag) => typeof nodeTag === 'string';
class JsxCatalogItem extends CatalogItem {
    /**
     * Parses Jsx code
     *
     * @param nodeTagOrModule A node tag or JsxCatalogItem module
     * @param props Properties of nodeTagOrModule
     * @param children Children of nodeTagOrModule
     */
    // eslint-disable-next-line consistent-return
    static parseJsx(nodeTagOrModule, props, ...children) {
        // Since the props param can be passed in as `null`, make sure we have an actual object to use
        const propsObject = props || {};
        if (isNodeTag(nodeTagOrModule)) {
            return JsxCatalogItem.createHtmlElement(nodeTagOrModule, propsObject, ...children);
        }
        // Instantiate module with options
        const instance = new nodeTagOrModule(propsObject?.options); // eslint-disable-line new-cap
        // Type guard - we already know that instance is a JsxCatalogItem but without explicitly checking,
        // TypeScript can't infer that a createElement call exists on instance
        if (isJsxCatalogItem(instance)) {
            const element = parseProps(instance.createElement(propsObject), propsObject);
            if (children) {
                return JsxCatalogItem.handleChildren(element, ...children);
            }
            return element;
        }
    }
    /**
     * Controller function for handling creation of HTMLElement
     *
     * @param {string} nodeTag Node tag of an HTMLElement
     * @param {JsxCatalogItemProps} props Properties to add onto creates HTMLElement
     * @param {Children[]} children Children of HTMLElement
     */
    static createHtmlElement(nodeTag, props, ...children) {
        let element;
        if (nodeTag === 'fragment') {
            element = document.createDocumentFragment();
        }
        else {
            element = document.createElement(nodeTag);
        }
        if (children) {
            element = JsxCatalogItem.handleChildren(element, ...children);
        }
        if (props) {
            element = parseProps(element, props);
        }
        return element;
    }
    /**
     * Handles addition of children onto passed element
     *
     * @param {HTMLElement} element Element to append children to
     * @param {Children[]} children Children to append onto element
     */
    static handleChildren(element, ...children) {
        children.forEach((child) => {
            /**
             * If child is an array, only allow for one array deep of processing.
             * Otherwise, recursion can go too deep and error out. Plus, if you have
             * arrays that are more than one level deep, then your logic needs to be revaluated
             */
            if (Array.isArray(child)) {
                child.forEach((childElement) => JsxCatalogItem.handleChild(element, childElement));
                return;
            }
            JsxCatalogItem.handleChild(element, child);
        });
        return element;
    }
    /**
     * Handles an individual child
     * @param {HTMLElement} element Element to append child to
     * @param {Children} child Child to append onto element
     */
    static handleChild(element, child) {
        if (isHtmlElement(child) || isDocumentFragment(child)) {
            element.appendChild(child);
            return;
        }
        if (typeof child === 'string') {
            const textNode = document.createTextNode(child);
            element.appendChild(textNode);
            return;
        }
        if (typeof child === 'number') {
            element.appendChild(document.createTextNode(child.toString(10)));
        }
    }
}
export default JsxCatalogItem;
